import { FC, useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';
import useRoiRequestTime from 'hooks/useRoiRequestTime';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

// @ts-ignore — extending Chart.js positioners
Tooltip.positioners.cursor = (_items, eventPosition) => ({
  x: eventPosition.x,
  y: eventPosition.y,
});

interface TimeOfRequestsProps {
  selectedDateRange: { fromDate: string; toDate: string };
  hourType: string;
  clinicIds?: string[];
}

const INSIDE_COLOR = '#B01BAE';
const OUTSIDE_COLOR = '#D8B4FE';
const LABELS = Array.from({ length: 24 }, (_, i) => String(i));

const TimeOfRequests: FC<TimeOfRequestsProps> = ({
  selectedDateRange,
  hourType,
  clinicIds,
}) => {
  const { data, isLoading, isValidating } = useRoiRequestTime({
    startDate: selectedDateRange.fromDate,
    endDate: selectedDateRange.toDate,
    hourType,
    clinicIds,
  });

  const { insideByHour, outsideByHour } = useMemo(() => {
    const inside = new Array(24).fill(0);
    const outside = new Array(24).fill(0);
    (data?.periodData?.timeOfRequest ?? []).forEach(
      ({
        hourLabel,
        totalAppointmentsDuringWorkingHour,
        totalAppointmentOutsideWorkingHour,
      }) => {
        const h = parseInt(hourLabel, 10);
        if (h >= 0 && h < 24) {
          inside[h] = totalAppointmentsDuringWorkingHour;
          outside[h] = totalAppointmentOutsideWorkingHour;
        }
      }
    );
    return { insideByHour: inside, outsideByHour: outside };
  }, [data]);

  const insideTotal = useMemo(
    () => insideByHour.reduce((sum, v) => sum + v, 0),
    [insideByHour]
  );
  const outsideTotal = useMemo(
    () => outsideByHour.reduce((sum, v) => sum + v, 0),
    [outsideByHour]
  );
  const grandTotal = insideTotal + outsideTotal;

  const maxValue = useMemo(
    () => Math.max(...insideByHour.map((v, i) => v + outsideByHour[i]), 1),
    [insideByHour, outsideByHour]
  );

  const chartData = useMemo(
    () => ({
      labels: LABELS,
      datasets: [
        {
          label: 'Inside Office Hours',
          data: insideByHour,
          backgroundColor: INSIDE_COLOR,
          borderRadius: 2,
          barThickness: 8,
        },
        {
          label: 'Outside Office Hours',
          data: outsideByHour,
          backgroundColor: OUTSIDE_COLOR,
          borderRadius: 2,
          barThickness: 8,
        },
      ],
    }),
    [insideByHour, outsideByHour]
  );

  return (
    <div className="p-2.4 shadow-elevation-06-hover bg-white">
      <div className="flex items-center justify-between mb-2.4">
        <span className="text-16 font-bold leading-[2.4rem]">
          Time of Requests
        </span>
        <div className="flex items-center gap-x-1.6 text-12 text-darkest-grey">
          <div className="flex items-center gap-x-0.6">
            <span
              className="w-0.8 h-0.8 rounded-full inline-block flex-shrink-0"
              style={{ backgroundColor: INSIDE_COLOR }}
            />
            <span>Inside Office Hours</span>
          </div>
          <div className="flex items-center gap-x-0.6">
            <span
              className="w-0.8 h-0.8 rounded-full inline-block flex-shrink-0"
              style={{ backgroundColor: OUTSIDE_COLOR }}
            />
            <span>Outside Office Hours</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 border border-light-grey rounded-[1.2rem] mb-2.4 overflow-hidden">
        <div className="p-2.4">
          <div className="text-14 font-bold text-darkest-grey leading-[2.1rem] mb-1.6">
            Inside Office Hours
          </div>
          <div className="text-30 font-bold text-magenta leading-[4rem]">
            {(isLoading || isValidating) ? '—' : insideTotal.toLocaleString()}
          </div>
        </div>
        <div className="p-2.4 border-l border-light-grey">
          <div className="text-14 font-bold text-darkest-grey leading-[2.1rem] mb-1.6">
            Outside Office Hours
          </div>
          <div className="text-30 font-bold text-magenta leading-[4rem]">
            {(isLoading || isValidating) ? '—' : outsideTotal.toLocaleString()}
          </div>
        </div>
        <div className="p-2.4 border-l border-light-grey">
          <div className="text-14 font-bold text-darkest-grey leading-[2.1rem] mb-1.6">
            Total
          </div>
          <div className="text-30 font-bold text-magenta leading-[4rem]">
            {(isLoading || isValidating) ? '—' : grandTotal.toLocaleString()}
          </div>
        </div>
      </div>

      {(isLoading || isValidating) ? (
        <div className="h-[52rem] flex items-center justify-center">
          <LoadingSpinner className="all-child:fill-magenta w-4 h-4" />
        </div>
      ) : (
        <div className="h-[52rem]">
          <Bar
            data={chartData}
            options={{
              indexAxis: 'y',
              maintainAspectRatio: false,
              interaction: { mode: 'index', intersect: true, axis: 'y' },
              plugins: {
                legend: { display: false },
                tooltip: {
                  enabled: true,
                  mode: 'index',
                  intersect: true,
                  axis: 'y',
                  callbacks: {
                    title: (items) => `Hour: ${items[0].label}:00`,
                    label: (item) =>
                      `${item.dataset.label}: ${(
                        item.raw as number
                      ).toLocaleString()}`,
                    footer: (items) =>
                      `Total: ${items
                        .reduce((sum, i) => sum + (i.raw as number), 0)
                        .toLocaleString()}`,
                  },
                },
              },
              scales: {
                x: {
                  stacked: true,
                  grid: {
                    color: '#E8E8E8',
                  },
                  ticks: {
                    color: '#373737',
                    font: { size: 12 },
                    precision: 0,
                    stepSize: 1,
                    callback: (value: any) =>
                      Number.isInteger(value) ? value.toLocaleString() : null,
                  },
                  max: Math.ceil(maxValue * 1.1),
                },
                y: {
                  stacked: true,
                  grid: { display: false },
                  ticks: {
                    color: '#373737',
                    font: { size: 11 },
                    callback: (_value: any, index: number) =>
                      index % 2 === 0 ? String(index) : null,
                  },
                  title: {
                    display: true,
                    text: 'Hour of Request',
                    color: '#373737',
                    font: { size: 11 },
                  },
                },
              },
            }}
          />
        </div>
      )}
    </div>
  );
};

export default TimeOfRequests;
